import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  ReactNode,
} from 'react';

export interface PreloadImageInfo {
  href: string;
  srcSet?: string;
  sizes?: string;
}

interface ImagePreloadContextValue {
  /**
   * 注册一个需要 preload 的图片（SSR 时调用）
   */
  addPreload: (info: PreloadImageInfo) => void;
  /**
   * 获取所有已注册的 preload 图片
   */
  getPreloads: () => PreloadImageInfo[];
}

const ImagePreloadContext = createContext<ImagePreloadContextValue | null>(null);

/**
 * ImagePreloadProvider
 *
 * 在 _app 或页面顶层包裹，用于收集 priority 图片的 preload 信息
 *
 * @example
 * // _app.tsx
 * import { ImagePreloadProvider } from '@/components/NextImage/ImagePreloadContext';
 *
 * function MyApp({ Component, pageProps }) {
 *   return (
 *     <ImagePreloadProvider>
 *       <Component {...pageProps} />
 *     </ImagePreloadProvider>
 *   );
 * }
 */
export function ImagePreloadProvider({ children }: { children: ReactNode }) {
  // 使用 ref 避免重复触发渲染
  const preloadsRef = useRef<PreloadImageInfo[]>([]);
  // 用 Set 去重
  const preloadSetRef = useRef<Set<string>>(new Set());

  const addPreload = useCallback((info: PreloadImageInfo) => {
    // 使用 href 作为唯一标识去重
    if (!preloadSetRef.current.has(info.href)) {
      preloadSetRef.current.add(info.href);
      preloadsRef.current.push(info);
    }
  }, []);

  const getPreloads = useCallback(() => preloadsRef.current, []);

  return (
    <ImagePreloadContext.Provider value={{ addPreload, getPreloads }}>
      {children}
    </ImagePreloadContext.Provider>
  );
}

/**
 * 获取 preload context（内部使用）
 */
export function useImagePreload(): ImagePreloadContextValue | null {
  return useContext(ImagePreloadContext);
}

/**
 * ImagePreloadHead
 *
 * 渲染所有收集到的 preload link 标签
 * 放在 Head 组件内部使用
 *
 * @example
 * // _document.tsx
 * import { ImagePreloadHead } from '@/components/NextImage/ImagePreloadContext';
 *
 * <Head>
 *   <ImagePreloadHead />
 * </Head>
 */
export function ImagePreloadHead() {
  const context = useContext(ImagePreloadContext);
  const preloads = context?.getPreloads() || [];

  return (
    <>
      {preloads.map((preload) => (
        <link
          key={preload.href}
          rel="preload"
          as="image"
          href={preload.href}
          // @ts-ignore - React 类型定义可能不包含这些属性，但浏览器支持
          imageSrcSet={preload.srcSet || undefined}
          imageSizes={preload.sizes || undefined}
        />
      ))}
    </>
  );
}

// ============================================
// 全局 Preload 收集器（用于 SSR 场景）
// ============================================

// 全局存储，SSR 时每个请求会重置
let globalPreloads: PreloadImageInfo[] = [];
let globalPreloadSet: Set<string> = new Set();

/**
 * 添加全局 preload（SSR 时使用）
 * 这是一个 escape hatch，当没有 Context 时的备选方案
 */
export function addGlobalPreload(info: PreloadImageInfo) {
  if (!globalPreloadSet.has(info.href)) {
    globalPreloadSet.add(info.href);
    globalPreloads.push(info);
  }
}

/**
 * 获取所有全局 preload
 */
export function getGlobalPreloads(): PreloadImageInfo[] {
  return globalPreloads;
}

/**
 * 重置全局 preload（每个 SSR 请求开始时调用）
 */
export function resetGlobalPreloads() {
  globalPreloads = [];
  globalPreloadSet = new Set();
}

/**
 * 生成 preload link 的 HTML 字符串
 * 用于直接注入到 HTML head 中
 */
export function generatePreloadLinksHTML(): string {
  return globalPreloads
    .map((preload) => {
      const srcSetAttr = preload.srcSet ? ` imagesrcset="${preload.srcSet}"` : '';
      const sizesAttr = preload.sizes ? ` imagesizes="${preload.sizes}"` : '';
      return `<link rel="preload" as="image" href="${preload.href}"${srcSetAttr}${sizesAttr} />`;
    })
    .join('\n');
}
